import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';
import { apiClient } from '@zaogig/auth';

// ─── Local sub-components ──────────────────────────

type BadgeTone = "gray" | "green" | "blue" | "dark" | "amber" | "red";

function Badge({ children, tone = "gray" }: { children: ReactNode; tone?: BadgeTone }) {
  const tones: Record<BadgeTone, string> = {
    gray: "bg-gray-50 text-gray-600 ring-gray-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    dark: "bg-gray-950 text-white ring-gray-950",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    red: "bg-red-50 text-red-700 ring-red-100",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
}

function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-3xl bg-white p-4 shadow-sm ring-1 ring-gray-100 ${className}`}>
      {children}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <Card className="min-h-[88px]">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-2 text-lg font-black text-gray-950">{value}</div>
    </Card>
  );
}

function MenuList({ items, onNavigate }: { items: string[]; onNavigate?: (item: string) => void }) {
  const handleClick = (item: string) => {
    const menuAction: Record<string, { message: string; tab?: string }> = {
      '发布任务': { message: '请在「企业」Tab 中使用岗位发布功能', tab: 'enterprise' },
      '排班管理': { message: '请在企业工作台查看岗位报名与排班状态', tab: 'enterprise' },
      '候选人池': { message: '请在企业工作台的报名列表处理候选人', tab: 'enterprise' },
      '门店管理': { message: '请从小程序企业入驻或门店入驻页面维护门店信息' },
      '成员权限': { message: '成员权限需由管理员在后台账号体系中配置' },
      '结算与账单': { message: '请在小程序企业结算页查看账单并确认结算' },
    };
    const action = menuAction[item] || { message: '请从企业工作台进入对应业务页面' };
    toast.info(action.message);
    if (action.tab && onNavigate) {
      onNavigate(action.tab);
    }
  };

  return (
    <Card>
      <div className="space-y-1">
        {items.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => handleClick(item)}
            className="flex w-full items-center justify-between rounded-2xl px-3 py-3 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <span>{item}</span>
            <span className="text-gray-300">›</span>
          </button>
        ))}
      </div>
    </Card>
  );
}

// ─── Page component ────────────────────────────────

export default function EnterpriseProfileCenter() {
  const [loading, setLoading] = useState(true);
  const [companyJobs, setCompanyJobs] = useState<any[]>([]);
  const [companyProfile, setCompanyProfile] = useState<any>(null);
  void companyJobs;

  const getExposureLevelText = (level: string) => {
    const levelMap: Record<string, string> = {
      'priority': '优先曝光',
      'normal': '正常曝光',
      'limited': '限制曝光',
      'blocked': '已封禁'
    };
    return levelMap[level] || '正常曝光';
  };

  const getExposureGradient = (level: string) => {
    const gradientMap: Record<string, string> = {
      'priority': 'from-emerald-500 to-blue-600',
      'normal': 'from-blue-500 to-indigo-600',
      'limited': 'from-amber-500 to-orange-600',
      'blocked': 'from-red-500 to-rose-600'
    };
    return gradientMap[level] || 'from-blue-500 to-indigo-600';
  };

  const data = {
    name: "塘口活动中心",
    avatar: "店",
    creditLevel: companyProfile?.credit_level || "A",
    creditScore: companyProfile?.credit_score || 84,
    exposureLevel: companyProfile?.exposure_level || "normal",
    status: "已认证企业",
    monthlyWorkers: "86 人次",
    arrivalRate: companyProfile?.worker_reuse_rate ? `${Math.round(companyProfile.worker_reuse_rate * 100)}%` : "95%",
    settlementAmount: "¥32,000",
    stores: ["塘口文创园", "赤坎老街", "开平会展中心"],
    members: ["店长", "HR", "调度人员", "财务"],
    tags: companyProfile?.tags || [],
    riskTags: companyProfile?.risk_tags || [],
    recommendReason: companyProfile?.recommend_reason || [],
    paymentStabilityScore: companyProfile?.payment_stability_score || 91,
    menu: ["发布任务", "排班管理", "候选人池", "门店管理", "成员权限", "结算与账单"],
  };

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        try {
          const result = await apiClient.get<{ jobs: any[]; total: number }>('/jobs/company/jobs?limit=10');
          setCompanyJobs(result.jobs);
        } catch (err) {
          console.log('使用模拟数据');
        }

        try {
          const mockEnterpriseId = 'ent-123456';
          const profile = await apiClient.get<any>(`/v1/profiles/enterprises/${mockEnterpriseId}`);
          setCompanyProfile(profile);
        } catch (err) {
          console.log('使用模拟画像数据');
        }
      } catch (err) {
        console.error('加载企业数据失败:', err);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] p-5 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[760px] bg-[#F7F9FC]">
      <div className="bg-white px-5 py-4">
        <p className="text-xs text-gray-400">企业端 · 企业微信登录</p>
        <h2 className="mt-1 text-lg font-black text-gray-950">企业中心</h2>
      </div>
      <div className="space-y-4 p-5">
        <Card>
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-100 text-xl font-black text-blue-700">
              {data.avatar}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-black text-gray-950">{data.name}</h3>
                <Badge tone="blue">信用 {data.creditLevel}</Badge>
              </div>
              <p className="mt-1 text-sm text-gray-500">{data.status} · 企业微信已绑定</p>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-3 gap-3">
          <MetricCard label="本月用工" value={data.monthlyWorkers} />
          <MetricCard label="到岗率" value={data.arrivalRate} />
          <MetricCard label="结算金额" value={data.settlementAmount} />
        </div>

        <Card>
          <div className="mb-3 flex items-center justify-between">
            <div className="font-black text-gray-950">门店 / 用工点</div>
            <Badge tone="blue">{data.stores.length} 个</Badge>
          </div>
          <div className="space-y-2">
            {data.stores.map((item) => (
              <div key={item} className="flex items-center justify-between rounded-2xl bg-gray-50 px-3 py-3 text-sm font-semibold text-gray-700">
                <span>{item}</span>
                <span className="text-gray-300">›</span>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div className="mb-3 flex items-center justify-between">
            <div className="font-black text-gray-950">成员与权限</div>
            <Badge tone="green">企业微信同步</Badge>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.members.map((item) => (
              <Badge key={item}>{item}</Badge>
            ))}
          </div>
        </Card>

        <Card>
          <div className="mb-3 flex items-center justify-between">
            <div className="font-black text-gray-950">信用资产</div>
            <Badge tone="dark">信用分 {data.creditScore}</Badge>
          </div>
          <div className={`rounded-2xl bg-gradient-to-br ${getExposureGradient(data.exposureLevel)} p-4 text-white`}>
            <div className="text-sm opacity-80">AI 曝光等级</div>
            <div className="mt-1 text-2xl font-black">{getExposureLevelText(data.exposureLevel)}</div>
            <p className="mt-2 text-sm opacity-80">
              {data.recommendReason?.length > 0 ? data.recommendReason.join('、') : '门店地点清晰、历史结算稳定、复用人员比例较高。'}
            </p>
          </div>
        </Card>

        {data.tags.length > 0 && (
          <Card>
            <div className="mb-3 flex items-center justify-between">
              <div className="font-black text-gray-950">企业标签</div>
              <Badge tone="green">可信企业</Badge>
            </div>
            <div className="flex flex-wrap gap-2">
              {data.tags.map((tag: string) => (
                <Badge key={tag} tone="green">{tag}</Badge>
              ))}
            </div>
          </Card>
        )}

        {data.riskTags.length > 0 && (
          <Card>
            <div className="mb-3 flex items-center justify-between">
              <div className="font-black text-gray-950">风险提示</div>
              <Badge tone={data.riskTags.length > 2 ? "red" : "amber"}>需注意</Badge>
            </div>
            <div className="flex flex-wrap gap-2">
              {data.riskTags.map((tag: string) => (
                <div key={tag} className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-100">
                  ! {tag}
                </div>
              ))}
            </div>
          </Card>
        )}

        <MenuList items={data.menu} />
      </div>
    </div>
  );
}
